/* BattleLiveDispatcher — tiny shell that decides whether to render the
 * new cinematic PvP arena or the legacy tap-combat LiveArena for a
 * given battle_id. PvP-tagged battles (mode:'friend' OR human_vs_human)
 * get the premium experience; world-boss / matchmaking battles keep
 * the existing tap-combat page.
 *
 * The whole point of this shell is to keep LiveArena untouched so
 * PvE tap combat can't regress while we ship the PvP upgrade.
 */
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import api from '../lib/api';
import LiveArena from './LiveArena';
import PvpBattleArena from './PvpBattleArena';

export default function BattleLiveDispatcher() {
  const { battleId } = useParams();
  const loc = useLocation();
  const preloaded = loc.state?.battle || null;
  const [battle, setBattle] = useState(preloaded);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (battle) return;
    api.get(`/battles/${battleId}`)
      .then(r => setBattle(r.data))
      .catch(() => setErr('not found'));
  }, [battleId, battle]);

  if (err) {
    return <div className="fixed inset-0 flex items-center justify-center bg-black text-white/50 text-sm">Battle not found.</div>;
  }
  if (!battle) {
    return <div className="fixed inset-0 flex items-center justify-center bg-black text-white/40 text-xs uppercase tracking-widest">Loading…</div>;
  }

  /* Routing rules (locked-in per user spec):
   *  - practice      → ALWAYS LiveArena (strictly PvE, even if a stray
   *                    human opponent slips through — defensive guard).
   *  - ranked/casual  → PvpBattleArena when the opponent is a real human;
   *                    LiveArena when AI fallback was used ("Play vs AI").
   *  - friend / duel  → PvpBattleArena (rival battles are their own stream).
   */
  const mode = String(battle.mode || 'practice').toLowerCase();
  const isDuelMode = mode === 'friend' || mode === 'duel';
  const isPvpMode = mode === 'ranked' || mode === 'casual';
  const isPvp =
    isDuelMode ||
    (isPvpMode && battle.human_vs_human === true);
  return isPvp ? <PvpBattleArena /> : <LiveArena />;
}
